/**
 * GA4 Event Tracking Utility
 *
 * Sends standard GA4 ecommerce-style events so we get funnel reporting,
 * audience building, and remarketing out of the box in Google Analytics.
 *
 * All events are fire-and-forget — never blocks the UI.
 */

import { trackActivity } from './activity';

// Extend window for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/** Safe gtag wrapper — no-ops if gtag isn't loaded */
function gtag(...args: unknown[]) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
}

// ── Entity / Campsite Detail Page View ──────────────────────────────
export interface ViewItemParams {
  entityId: number | string;
  entityName: string;
  entityType?: string;       // campsite | accommodation | tours
  region?: string;
  state?: string;
  town?: string;
  rating?: number;
  isPremium?: boolean;
  displaySrc?: string;       // Bookeasy | Manual
}

export function trackViewItem(params: ViewItemParams) {
  trackActivity('campsite_view', { entityId: params.entityId, name: params.entityName });
  gtag('event', 'view_item', {
    currency: 'AUD',
    items: [{
      item_id: String(params.entityId),
      item_name: params.entityName,
      item_category: params.entityType || 'campsite',
      item_category2: params.region || '',
      item_category3: params.state || '',
      item_category4: params.town || '',
      item_brand: params.displaySrc || 'Manual',
      price: 0,
    }],
    // Custom dimensions
    entity_type: params.entityType || 'campsite',
    entity_rating: params.rating,
    is_premium: params.isPremium || false,
  });
}

// ── Campsite Card Click (search results / recommendations) ──────────
export interface SelectItemParams {
  entityId: number | string;
  entityName: string;
  entityType?: string;
  listName: string;           // 'search_results' | 'nearby' | 'recommendations' | 'trip_stops'
  index?: number;             // position in list
}

export function trackSelectItem(params: SelectItemParams) {
  gtag('event', 'select_item', {
    item_list_name: params.listName,
    items: [{
      item_id: String(params.entityId),
      item_name: params.entityName,
      item_category: params.entityType || 'campsite',
      index: params.index ?? 0,
    }],
  });
}

// ── Search ──────────────────────────────────────────────────────────
export interface SearchParams {
  searchTerm: string;
  typeFilter?: string;        // All | Free Camping | Activities | etc
  resultCount: number;
  radius?: number;
  hasFeatureFilters?: boolean;
}

export function trackSearch(params: SearchParams) {
  // Mirrored to first-party logging for the admin insights dashboard.
  trackActivity('search', {
    term: params.searchTerm,
    resultCount: params.resultCount,
    meta: {
      type: params.typeFilter || 'All',
      radius: params.radius ?? null,
      features: !!params.hasFeatureFilters,
    },
  });
  gtag('event', 'search', {
    search_term: params.searchTerm,
    // Custom dimensions
    search_type_filter: params.typeFilter || 'All',
    search_result_count: params.resultCount,
    search_radius: params.radius,
    search_has_feature_filters: params.hasFeatureFilters || false,
  });
}

// ── Booking / Lead Generation ───────────────────────────────────────
export interface GenerateLeadParams {
  entityId: number | string;
  entityName: string;
  entityType?: string;
  leadType: 'booking_link' | 'phone_reveal' | 'website_link';
  bookingUrl?: string;
}

export function trackGenerateLead(params: GenerateLeadParams) {
  gtag('event', 'generate_lead', {
    currency: 'AUD',
    value: params.leadType === 'booking_link' ? 1 : 0.5,
    // Custom dimensions
    entity_id: String(params.entityId),
    entity_name: params.entityName,
    entity_type: params.entityType || 'campsite',
    lead_type: params.leadType,
  });
}

// ── Add to Trip ─────────────────────────────────────────────────────
export interface AddToTripParams {
  entityId: number | string;
  entityName: string;
  entityType?: string;
  tripStopCount: number;      // how many stops in the trip after adding
}

export function trackAddToTrip(params: AddToTripParams) {
  gtag('event', 'add_to_cart', {
    currency: 'AUD',
    value: 0,
    items: [{
      item_id: String(params.entityId),
      item_name: params.entityName,
      item_category: params.entityType || 'campsite',
    }],
    // Custom dimensions
    trip_stop_count: params.tripStopCount,
  });
}

// ── Share ───────────────────────────────────────────────────────────
export interface ShareParams {
  entityId: number | string;
  entityName: string;
  method: string;             // 'copy_link' | 'native_share' | 'whatsapp' | etc
}

export function trackShare(params: ShareParams) {
  gtag('event', 'share', {
    method: params.method,
    content_type: 'campsite',
    item_id: String(params.entityId),
    // Custom
    entity_name: params.entityName,
  });
}

// ── Chat Assistant ──────────────────────────────────────────────────
export function trackChatOpen() {
  gtag('event', 'chat_open', {
    event_category: 'engagement',
  });
}

export function trackChatMessage(messageType: 'user' | 'assistant') {
  gtag('event', 'chat_message', {
    event_category: 'engagement',
    message_type: messageType,
  });
}

// ── Filter / Tab Change ─────────────────────────────────────────────
export function trackFilterChange(filterType: string, filterValue: string) {
  gtag('event', 'filter_change', {
    event_category: 'engagement',
    filter_type: filterType,  // 'tab' | 'feature' | 'radius'
    filter_value: filterValue,
  });
}

// ── Map Interaction ─────────────────────────────────────────────────
export function trackMapInteraction(action: string) {
  gtag('event', 'map_interaction', {
    event_category: 'engagement',
    map_action: action,  // 'zoom' | 'pan' | 'fullscreen' | 'marker_click'
  });
}

// ── Weather Forecast ───────────────────────────────────────────────
export function trackWeatherExpand(campsiteName: string) {
  gtag('event', 'weather_expand', {
    event_category: 'engagement',
    entity_name: campsiteName,
  });
}

// ── Availability Calendar ───────────────────────────────────────────
export function trackAvailabilityCalendarOpen(params: {
  operatorId: number;
  product: string;
  entityName?: string;
}) {
  gtag('event', 'availability_calendar_open', {
    event_category: 'engagement',
    operator_id: params.operatorId,
    product: params.product,
    entity_name: params.entityName || '',
  });
}
